import { CertificationTooltipComponent, CertificationTooltipProps } from '@tableau/certification-watermark';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { SvgSpacesMediumDs } from '../../../../gen-src/icons/SpacesMediumDs';
import { SvgSpacesMediumDsCertified } from '../../../../gen-src/icons/SpacesMediumDsCertified';
import { SvgSpacesMediumExtract } from '../../../../gen-src/icons/SpacesMediumExtract';
import { SvgSpacesMediumExtractCertified } from '../../../../gen-src/icons/SpacesMediumExtractCertified';
import { getCertifierPropsFromCertifier } from '../../CertificationWidget';
import { TableauUser } from '../../UserDisplayComponent';
import { FeatureFlagsContext } from '../../featureFlags/FeatureFlagsContext';
import { divWithStyle } from '../../style/LayoutComponents';
import { FormattedMarkupWrapperInTooltip } from '../../style/MarkupStyles';
import { SiteUrlsContext } from '../../urls/SiteUrlsContext';

const CertifiedIconWrapper = divWithStyle('CertifiedIconWrapper', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface DatasourceTypeCellProps {
  readonly isCertified: boolean;
  readonly hasExtracts: boolean;
  readonly certifier: TableauUser | null;
  readonly certificationNote?: string;
  readonly extractLastUpdateTime?: Date;
  readonly linksDisabled?: boolean;
  readonly zIndex?: number;
}

/** If the data source is certified, the certifier isn't null, which we rely upon when computing the tooltip props. */
type CertifiedDatasourceTypeCellProps =
  DatasourceTypeCellProps & { certifier: NonNullable<DatasourceTypeCellProps['certifier']> };

function isCertified(props: DatasourceTypeCellProps): props is CertifiedDatasourceTypeCellProps {
  return props.isCertified && !!props.certifier;
}

function getCertifiedIconWithWrapper(hasExtracts: boolean) {
  return (
    <CertifiedIconWrapper>
      {hasExtracts ? <SvgSpacesMediumExtractCertified /> : <SvgSpacesMediumDsCertified />}
    </CertifiedIconWrapper>
  );
}

function getCertificationTooltipProps(
  markupEditingEnabled: boolean,
  props: CertifiedDatasourceTypeCellProps, urls?: SiteUrls,
): CertificationTooltipProps {

  const certificationNote = props.certificationNote || '';
  const note = markupEditingEnabled ? <FormattedMarkupWrapperInTooltip markup={certificationNote} /> : certificationNote;

  const certificationTooltipProps: CertificationTooltipProps = {
    certifier: getCertifierPropsFromCertifier(props.certifier, urls),
    note: note,
    icon: getCertifiedIconWithWrapper(props.hasExtracts),
    showExtractRefresh: true,
    date: props.extractLastUpdateTime,
    linksDisabled: props.linksDisabled || false,
    zIndex: props.zIndex || undefined,
  };

  return certificationTooltipProps;
}

export function DatasourceTypeCell(props: DatasourceTypeCellProps): JSX.Element {
  const { markupEditingEnabled, dqcertificationVisualDesignEnabled } = React.useContext(FeatureFlagsContext);

  if (!dqcertificationVisualDesignEnabled && isCertified(props)) {
    if (props.linksDisabled) {
      return <CertificationTooltipComponent {...getCertificationTooltipProps(markupEditingEnabled, props)} />;
    } else {
      return (
        <SiteUrlsContext.Consumer>
          {urls => <CertificationTooltipComponent {...getCertificationTooltipProps(markupEditingEnabled, props, urls)} />}
        </SiteUrlsContext.Consumer>
      );
    }
  } else {
    if (props.hasExtracts) {
      return <SvgSpacesMediumExtract />;
    } else {
      return <SvgSpacesMediumDs />;
    }
  }
}

// tslint:disable-next-line: no-shadowed-variable
export function GenerateCertificationTooltipComponent(isCertified: boolean, lastExtract: string | null,
  hasExtracts: boolean, certifier: TableauUser | null,
  certificationNote: string | undefined, linksDisabled?: boolean, zIndex?: number) {

  const date = lastExtract ? new Date(lastExtract) : undefined;
  const props: DatasourceTypeCellProps = {
    isCertified: isCertified,
    hasExtracts: hasExtracts,
    certifier: certifier,
    extractLastUpdateTime: date,
    certificationNote: certificationNote,
    linksDisabled: linksDisabled,
    zIndex: zIndex,
  };

  return (
    <DatasourceTypeCell {...props} />
  );
}
